/**
 * Singer-friendly utilities for ChurchMedley
 * Translates technical music concepts to intuitive labels for singers
 */

export function isSinger(user) {
  return user?.role === 'singer';
}

export function isMusicianOrAdmin(user) {
  return user?.role === 'musician' || user?.role === 'admin';
}

export function bpmToTempoLabel(bpm) {
  if (!bpm) return null;
  if (bpm < 70) return 'Muy lenta';
  if (bpm < 90) return 'Lenta';
  if (bpm < 110) return 'Moderada';
  if (bpm < 130) return 'Alegre';
  return 'Rápida';
}

export function compatibilityToLabel(level) {
  const map = {
    perfect: { text: 'Fluye perfecto', color: 'bg-green-100 text-green-800' },
    excellent: { text: 'Muy buena transición', color: 'bg-blue-100 text-blue-800' },
    good: { text: 'Transición aceptable', color: 'bg-yellow-100 text-yellow-800' },
    challenging: { text: 'Cambio notable', color: 'bg-red-100 text-red-800' },
  };
  return map[level] || { text: level, color: 'bg-gray-100 text-gray-800' };
}

export function stripChords(text) {
  if (!text) return '';
  // Remove [Chord] inline format — only actual chords, not section labels like [Coro], [Verso]
  let result = text.replace(/\[([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*)\]/g, '');
  // Remove chord-above lines (lines with only chord names and spaces)
  result = result.replace(/^[ \t]*([A-G][#b]?(?:m|maj|min|dim|aug|sus|add|7|9|11|13|\/[A-G][#b]?)*[ \t]*)+$/gm, '');
  // Clean up double blank lines
  result = result.replace(/\n{3,}/g, '\n\n');
  return result;
}

/**
 * Detect if a line is a chord-only line (chords placed above lyrics).
 * Matches lines like "  A        D" or "F#m   Bm   Em"
 */
export function isChordLine(line) {
  if (!line || !line.trim()) return false;
  // A chord line contains only chord names and whitespace
  const chordPattern = /^[ \t]*([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*[ \t]*)+$/;
  return chordPattern.test(line);
}

/**
 * Parse a space-padded chord line into position+chord pairs.
 * E.g. "  A        D" → [{pos: 2, chord: "A"}, {pos: 11, chord: "D"}]
 */
export function parseChordPositions(chordLine) {
  const chords = [];
  if (!chordLine) return chords;
  const regex = /([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*)/g;
  let match;
  while ((match = regex.exec(chordLine)) !== null) {
    chords.push({ pos: match.index, chord: match[1] });
  }
  return chords;
}

/**
 * Convert a chord symbol to its Spanish name.
 * E.g. "D" → "Re mayor", "F#m" → "Fa# menor", "A7" → "La 7"
 */
const NOTE_NAMES_ES = {
  'C': 'Do', 'C#': 'Do#', 'Db': 'Reb',
  'D': 'Re', 'D#': 'Re#', 'Eb': 'Mib',
  'E': 'Mi',
  'F': 'Fa', 'F#': 'Fa#', 'Gb': 'Solb',
  'G': 'Sol', 'G#': 'Sol#', 'Ab': 'Lab',
  'A': 'La', 'A#': 'La#', 'Bb': 'Sib',
  'B': 'Si',
};

const SUFFIX_NAMES_ES = {
  '': 'mayor',
  'm': 'menor',
  '7': '7 (séptima)',
  'm7': 'menor 7',
  'maj7': 'mayor 7',
  'dim': 'disminuido',
  'aug': 'aumentado',
  'sus2': 'suspendido 2',
  'sus4': 'suspendido 4',
  'add9': 'add 9',
  '9': '9 (novena)',
  '6': '6 (sexta)',
  'm6': 'menor 6',
  '11': '11 (oncena)',
  '13': '13 (trecena)',
};

export function chordToSpanish(chord) {
  if (!chord) return '';
  const match = chord.match(/^([A-G][#b]?)(.*)/);
  if (!match) return chord;
  const [, root, suffix] = match;
  // Handle slash chords like D/F#
  const slashIdx = suffix.indexOf('/');
  let mainSuffix = suffix;
  let bassNote = '';
  if (slashIdx !== -1) {
    mainSuffix = suffix.substring(0, slashIdx);
    const bassChord = suffix.substring(slashIdx + 1);
    bassNote = ` con bajo en ${NOTE_NAMES_ES[bassChord] || bassChord}`;
  }
  const rootName = NOTE_NAMES_ES[root] || root;
  const suffixName = SUFFIX_NAMES_ES[mainSuffix] || mainSuffix;
  return `${rootName} ${suffixName}${bassNote}`.trim();
}

export const ENERGY_OPTIONS = [
  { value: 'intimate', label: 'Íntima y suave', count: 3 },
  { value: 'moderate', label: 'Moderada', count: 4 },
  { value: 'energetic', label: 'Energética y celebrativa', count: 6 },
];
